import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CpfRevelavel, DataTable, EmptyState, FilterBar, FilterChips, MaisFiltros, MaisFiltrosGrupo, OlhoBotao,
  Pagination, Pill, SelectField, Button, type Column, type MaisFiltrosResumo,
} from "@atlas/ui/web";
import { TotalEmFolha, ValorSolicitado } from "../../../components/ValorContrato";
import { CronogramaLegadoModal } from "../../../components/CronogramaLegadoModal";
import { InformarDadosLegadoModal } from "../../../components/InformarDadosLegadoModal";
import { OfertarRefinanciamentoModal } from "../../../components/RefinanciamentoBancoModais";
import { DesaverbarContratoModal } from "../../../components/DesaverbarContratoModal";
import { bancoPodeRefinanciar, readBancoPermissoesDaSessao } from "../../../lib/banco-perms";
import type { DadosInformadosDoContrato } from "../../../lib/dados-informados";
import { aplicarFiltros, contagens, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../../lib/recorte";
import { matchAnyKeys } from "../../../lib/text-search";
import { useFiltros } from "../../../lib/use-filtros";
import { downloadCsv, downloadJson } from "../../../lib/csv";
import { atlas } from "../../../lib/sdk";
import { proximaParcelaDetalhe } from "./proxima-parcela";
import {
  PRODUTO_LABEL,
  fmtBRL,
  getBancoPerfil,
  type BancoProduto,
} from "../../../lib/banco-propostas";
import {
  CONTRATO_STATUS_LABEL,
  type Contrato,
  type ContratoStatus,
} from "../../../lib/banco-carteira";

function statusPill(s: ContratoStatus): "emdia" | "aceita" | "rejeitada" {
  return s === "em_dia" ? "emdia" : s === "quitado" ? "aceita" : "rejeitada";
}

/** Backend situacao -> ContratoStatus da UI. Contratos ainda em analise (reserva)
 *  nao entram na carteira — so os averbados/ativos aparecem aqui. */
function mapSituacaoBackend(situacao: string): ContratoStatus | null {
  const t = situacao.toLowerCase();
  if (t.includes("quitad")) return "quitado";
  if (t.includes("inadimpl")) return "inadimplente";
  // "libera" cobre "Liberada" — status usado pelo backend quando o banco
  // aprova a proposta e o contrato entra em vigor. Sem esse match, o
  // contrato sumia da carteira/ADF (ficava so na fila de propostas).
  if (t.includes("ativo") || t.includes("averb") || t.includes("libera")) return "em_dia";
  return null; // aguardando/cancelada/recusada nao vira contrato de carteira
}

/** Parseia data em ISO 8601 ou DD/MM/YYYY (formato BR das fixtures do backend).
 *  Retorna 0 se nao parsear — sort trata como "muito antigo". */
function parseLancamento(raw: string | null | undefined): number {
  if (!raw) return 0;
  const iso = new Date(raw).getTime();
  if (!Number.isNaN(iso)) return iso;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
  return 0;
}

/** Linha da carteira = Contrato + status do ADF na folha da prefeitura (folhaStatus),
 *  pra mostrar "Aplicada em folha" quando a prefeitura confirma (Passo 6). */
type CarteiraRow = Contrato & {
  /** CPF completo (contrato do proprio banco). Vai no CSV/JSON; na tela, so' com o olho aberto. */
  cpf?: string;
  folhaStatus?: "recebida" | "aplicada" | "falha";
  /** R2 key do CCB real (anexado pelo banco em /banco/propostas/:id). null se
   *  o contrato foi criado sem anexo — nesse caso a coluna CCB mostra "—". */
  ccbKey?: string | null;
  /** `legado` = o banco e' o credor, mas nao originou aqui (ADR-0003). */
  origem?: "atlas" | "legado";
  /** `false` = legado sem informe: `valor` e' zero e NAO vale — foi o "varios
   *  emprestimos com 0" do teste de 10/09/2026. */
  dadosFinanceirosConhecidos?: boolean;
  /** Feature 012: quem informou principal e taxa do legado, e quando. */
  dadosInformados?: DadosInformadosDoContrato | null;
  cronogramaPendente?: boolean;
  parcelasPagas?: number;
};

/** A forma que `ValorSolicitado`/`TotalEmFolha` leem, a partir da linha. */
function valoresDe(r: CarteiraRow) {
  return {
    valorParcela: r.valorParcela,
    totalParcelas: r.parcelas,
    valorFinanciado: r.valor,
    dadosFinanceirosConhecidos: r.dadosFinanceirosConhecidos,
    origem: r.origem,
    dadosInformados: r.dadosInformados,
  };
}

/**
 * O que a coluna "Folha" de fato distingue — TRES baldes, nao quatro.
 *
 * `folhaStatus` tem tres valores mais `undefined`, mas a coluna sempre colapsou
 * `undefined` e `"recebida"` em "Aguardando folha": do ponto de vista do banco
 * as duas dizem a mesma coisa (a prefeitura ainda nao aplicou). O filtro sai
 * DAQUI para nao oferecer uma distincao que a tela nao mostra — chip "Recebida"
 * separado de "Aguardando" faria o operador procurar diferenca entre duas
 * linhas visualmente identicas.
 */
export type FolhaBalde = "aguardando" | "aplicada" | "falha";

export function folhaBalde(f?: "recebida" | "aplicada" | "falha"): FolhaBalde {
  if (f === "aplicada") return "aplicada";
  if (f === "falha") return "falha";
  return "aguardando";
}

const FOLHA_ROTULO: Record<FolhaBalde, { label: string; variant: "emdia" | "aceita" | "rejeitada" }> = {
  aplicada: { label: "Aplicada em folha", variant: "emdia" },
  falha: { label: "Falha na folha", variant: "rejeitada" },
  aguardando: { label: "Aguardando folha", variant: "aceita" },
};

function folhaPill(f?: "recebida" | "aplicada" | "falha"): { label: string; variant: "emdia" | "aceita" | "rejeitada" } {
  return FOLHA_ROTULO[folhaBalde(f)];
}

export function BancoCarteira() {
  const perfil = getBancoPerfil();
  // Sem filtro de convenio aqui: a carteira segue o CONVENIO ATIVO selecionado
  // no topo (canto sup. esquerdo). O backend ja devolve so os contratos do
  // convenio ativo — mostrar todos/outros aqui era redundante e confuso
  // (cliente 30/07/2026).
  const [produto, setProduto] = useState<"" | BancoProduto>("");
  const [status, setStatus] = useState<"" | ContratoStatus>("");
  // STATUS DO CONTRATO x RESULTADO NA FOLHA sao duas perguntas, e ate 04/09/2026
  // a tela mostrava as duas em coluna e filtrava so' a primeira. Sao saude
  // financeira (em dia / inadimplente / quitado) e execucao (a prefeitura
  // aplicou?) — um contrato pode estar "em dia" e ter falhado na folha do mes,
  // e era exatamente esse cruzamento que nao dava para ver.
  const [folha, setFolha] = useState<"" | FolhaBalde>("");
  const [busca, setBusca] = useState("");
  // Um olho para a tabela inteira: CPF mascarado por padrao (diretriz de 14/10/2026).
  const [cpfsRevelados, setCpfsRevelados] = useState(false);
  // O legado cujos dados o banco vai informar (feature 012). E' ele quem tem a
  // cedula — o botao so' aparece em contrato legado deste banco.
  const [informar, setInformar] = useState<CarteiraRow | null>(null);
  const [cronograma, setCronograma] = useState<CarteiraRow | null>(null);
  const [ofertarRefin, setOfertarRefin] = useState<string | null>(null);
  const [desaverbar, setDesaverbar] = useState<CarteiraRow | null>(null);
  const [avisoRefin, setAvisoRefin] = useState<string | null>(null);
  const [avisoDesaverbar, setAvisoDesaverbar] = useState<string | null>(null);
  const podeRefinanciar = useMemo(() => bancoPodeRefinanciar(readBancoPermissoesDaSessao()), []);
  const qc = useQueryClient();

  // Backend: contratos reais criados quando o banco aprovou propostas do
  // servidor. Poll 5s pra pegar novas aprovacoes em quase-tempo-real.
  const q = useQuery({
    queryKey: ["banco", "contratos-api"],
    queryFn: () => atlas.banco.contratos(),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
  });

  const contratosBackend: CarteiraRow[] = useMemo(() => {
    const list = q.data?.contratos ?? [];
    return list
      .map((ct): CarteiraRow | null => {
        const s = mapSituacaoBackend(ct.situacao);
        if (!s) return null;
        const tipo = ct.tipoContrato?.toLowerCase() ?? "";
        const produtoUi: BancoProduto = tipo.includes("portab") || tipo.includes("refin")
          ? "portabilidade"
          : tipo.includes("econsig") || tipo.includes("cartao")
            ? "cartao"
            : "emprestimo";
        return {
          idUnico: ct.adf,
          cpfMasked: ct.cpfMasked,
          cpf: ct.cpf,
          nome: ct.nome,
          convenio: ct.convenio,
          matricula: ct.matricula,
          produto: produtoUi,
          valor: ct.valorFinanciado,
          parcelas: ct.totalParcelas,
          valorParcela: ct.valorParcela,
          status: s,
          proximaParcela: ct.cronogramaPendente ? "Cronograma pendente" : proximaParcelaDetalhe(ct),
          // Prioridade: atualizadoEm (evento mais recente, ex.: acabou de
          // ser aprovada) > lancamento (data do contrato) > NOW. Assim
          // contratos recem-aprovados sobem pro topo em vez de ficarem
          // ancorados na data do contrato original.
          averbadoEm: ct.atualizadoEm ?? (() => {
            const t = parseLancamento(ct.lancamento);
            return t > 0 ? new Date(t).toISOString() : new Date().toISOString();
          })(),
          // Nao existe URL publica de CCB — o arquivo real esta em R2 privado e
          // e acessado via atlas.banco.fetchCcbBlob(ccbKey). Antes esta linha
          // apontava pra "https://formaliza.banco.com.br/ccb/..." que era 404.
          ccbUrl: "",
          ccbKey: ct.ccbKey ?? null,
          folhaStatus: ct.folhaStatus, // Passo 6: prefeitura confirmou em folha?
          origem: ct.origem,
          cronogramaPendente: ct.cronogramaPendente,
          parcelasPagas: ct.parcelasPagas,
          dadosFinanceirosConhecidos: ct.dadosFinanceirosConhecidos,
          dadosInformados: ct.dadosInformados ?? null,
        };
      })
      .filter((c): c is CarteiraRow => c !== null);
  }, [q.data]);

  /** A carteira inteira do convenio ativo, sem filtro nenhum. */
  const base = useMemo(() => {
    // Fonte unica = backend. Antes concatenavamos CARTEIRA_SEED (Sonia,
    // Paulo, Regina, etc. hardcoded) — visivel para QUALQUER banco logado,
    // incluindo recem criado pela averbadora que deveria estar zerado.
    // Removido: um banco so ve os contratos que ele proprio averbou.
    const byId = new Map<string, CarteiraRow>();
    for (const c of contratosBackend) byId.set(c.idUnico, c);
    return [...byId.values()]
      // Recentes no topo — ordena por averbadoEm desc. Aceita ISO e DD/MM/YYYY
      // (as fixtures do backend usam formato BR "18/03/2026", que new Date()
      // sozinho retorna NaN e joga o contrato pro fim da lista).
      .sort((a, b) => parseLancamento(b.averbadoEm) - parseLancamento(a.averbadoEm));
  }, [contratosBackend]);

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);

  // FILTROS NOMEADOS — sem nome nao existe `exceto` (FR-010), e o numero de um
  // chip tem de dizer "quantos eu veria se clicasse aqui".
  //
  // Busca SEM debounce: o recorte e' no cliente e instantaneo. Ela cobre ADF,
  // nome, CPF e matricula — os quatro identificadores que a grade ja exibia e
  // que, ate aqui, so' davam para achar virando pagina.
  const termo = busca.trim();
  const filtros = useMemo(() => ({
    produto: produto ? (c: CarteiraRow) => c.produto === produto : null,
    status: status ? (c: CarteiraRow) => c.status === status : null,
    folha: folha ? (c: CarteiraRow) => folhaBalde(c.folhaStatus) === folha : null,
    busca: termo
      ? (c: CarteiraRow) => matchAnyKeys(c, termo, ["idUnico", "nome", "cpfMasked", "cpf", "matricula"])
      : null,
  }), [produto, status, folha, termo]);

  const contratos = useMemo(() => aplicarFiltros(base, filtros), [base, filtros]);
  const semStatus = useMemo(() => aplicarFiltros(base, filtros, "status"), [base, filtros]);
  const semFolha = useMemo(() => aplicarFiltros(base, filtros, "folha"), [base, filtros]);
  const semProduto = useMemo(() => aplicarFiltros(base, filtros, "produto"), [base, filtros]);
  const contStatus = useMemo(() => contagens(semStatus, (c) => c.status), [semStatus]);
  const contFolha = useMemo(() => contagens(semFolha, (c) => folhaBalde(c.folhaStatus)), [semFolha]);
  const contProduto = useMemo(() => contagens(semProduto, (c) => c.produto), [semProduto]);

  const filtrosUi = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "situacao", ligado: status !== "", limpar: () => setStatus("") },
      { nome: "folhaStatus", ligado: folha !== "", limpar: () => setFolha("") },
      { nome: "produto", ligado: produto !== "", limpar: () => setProduto("") },
    ],
    setPagina,
  );
  const { filtrar, temFiltro } = filtrosUi;

  /** O que esta ligado DENTRO do painel, para o recorte nao ficar invisivel. */
  const resumoAvancado: MaisFiltrosResumo[] = produto
    ? [{ rotulo: "Produto", valor: PRODUTO_LABEL[produto], limpar: () => filtrar(setProduto)("") }]
    : [];

  const daPagina = recortar(contratos, pagina, porPagina);

  const exportRows = () =>
    contratos.map((c) => ({
      idUnico: c.idUnico,
      // Completo: relatorio vai sem censura, no escopo do banco (14/10/2026).
      cpf: c.cpf || c.cpfMasked,
      nome: c.nome,
      convenio: c.convenio,
      matricula: c.matricula,
      produto: PRODUTO_LABEL[c.produto],
      // Vazio, e nao zero, quando a Atlas nao conhece o principal — o CSV nao
      // pode afirmar "0" onde a tela mostra "—".
      valorSolicitado: c.dadosFinanceirosConhecidos === false ? "" : c.valor,
      totalEmFolha: c.cronogramaPendente ? '' : Math.round(c.valorParcela * c.parcelas * 100) / 100,
      parcelas: c.cronogramaPendente ? '' : c.parcelas,
      cronograma: c.cronogramaPendente ? 'Pendente' : 'Informado',
      valorParcela: c.valorParcela,
      status: CONTRATO_STATUS_LABEL[c.status],
      proximaParcela: c.proximaParcela,
      // ADF = idUnico do contrato (mesmo dado, mesmo campo no domain).
      adf: c.idUnico,
      // No CSV, exporta o nome do arquivo CCB (ultima parte do R2 key) ou
      // "—" quando nao ha CCB anexado. Antes exportava uma URL fake que nunca
      // resolvia — usuario baixava CSV e todos os links davam 404.
      ccb: c.ccbKey ? (c.ccbKey.split("/").pop() ?? c.ccbKey) : "—",
    }));

  const columns: Column<CarteiraRow>[] = [
    { key: "status", header: "Status", render: (r) => <Pill variant={statusPill(r.status)}>{CONTRATO_STATUS_LABEL[r.status]}</Pill> },
    {
      key: "folha",
      header: "Folha",
      render: (r) => {
        const f = folhaPill(r.folhaStatus);
        return <Pill variant={f.variant}>{f.label}</Pill>;
      },
    },
    // ADF = ID unico do contrato no domain. Antes tinham duas colunas
    // apontando pro mesmo dado (uma real, outra derivada de localStorage);
    // agora e' uma so, sempre presente. Cliente 28/07/2026.
    { key: "adf", header: "ADF", mono: true, render: (r) => r.idUnico },
    {
      key: "nome",
      header: "Servidor",
      render: (r) => (
        <>
          <div>{r.nome}</div>
          <div style={{ fontSize: 11, color: "var(--text-dim)" }}>
            <CpfRevelavel cpf={r.cpf} cpfMasked={r.cpfMasked} revelado={cpfsRevelados} /> / {r.matricula}
          </div>
        </>
      ),
    },
    { key: "convenio", header: "Convênio" },
    // O legado sem informe mostrava "R$ 0,00" aqui — foi o "varios emprestimos
    // com 0" do teste de 10/09/2026. Agora e' "—" com o selo, e o total que a
    // folha desconta tem coluna propria (feature 012).
    { key: "valor", header: "Valor solicitado", align: "right", render: (r) => <ValorSolicitado contrato={valoresDe(r)} formatar={fmtBRL} /> },
    { key: "parcelas", header: "Parcelas", align: "right", render: (r) => r.cronogramaPendente ? <><Pill variant="pendente">Cronograma pendente</Pill><div>{fmtBRL(r.valorParcela)} por mês</div></> : `${r.parcelas}x de ${fmtBRL(r.valorParcela)}` },
    { key: "totalEmFolha", header: "Total em folha", align: "right", render: (r) => <TotalEmFolha contrato={valoresDe(r)} formatar={fmtBRL} /> },
    { key: "proximaParcela", header: "Próx. parcela", render: (r) => r.proximaParcela },
    {
      key: "ccb",
      header: "CCB",
      render: (r) => <CcbCell ccbKey={r.ccbKey ?? null} />,
    },
    // So' o legado tem acao: e' o banco que tem a cedula dele. O contrato da
    // Atlas ja nasceu com ela. Perfil de consulta nao informa.
    {
      key: "acoes",
      header: "",
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          {r.origem === "legado" && perfil.perms.aprovacao ? (
            <>
            <Button size="sm" variant="ghost" onClick={() => setCronograma(r)}>{r.cronogramaPendente ? "Completar cronograma" : "Corrigir cronograma"}</Button>
            <Button size="sm" variant="ghost" disabled={r.cronogramaPendente} onClick={() => setInformar(r)}>
              {r.dadosInformados ? "Corrigir dados" : "Informar dados"}
            </Button>
            </>
          ) : null}
          {podeRefinanciar && r.status === "em_dia" ? (
            <Button size="sm" variant="ghost" onClick={() => setOfertarRefin(r.idUnico)}>
              Refinanciar
            </Button>
          ) : null}
          {perfil.perms.aprovacao && r.status !== "quitado" ? (
            <Button size="sm" variant="ghost" onClick={() => setDesaverbar(r)}>
              Desaverbar
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Carteira
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Meus contratos</h1>
          <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 640 }}>
            Contratos averbados por convênio. O <strong>ID único</strong> é a chave de conciliação de cada operação.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <OlhoBotao rotulo="CPFs" revelado={cpfsRevelados} onAlternar={() => setCpfsRevelados((v) => !v)} />
          {perfil.perms.exportacao ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => downloadCsv("carteira-consignataria.csv", exportRows())}>Exportar CSV</Button>
              <Button variant="ghost" size="sm" onClick={() => downloadJson("carteira-consignataria.json", exportRows())}>Exportar JSON</Button>
            </>
          ) : (
            <span style={{ fontSize: 12, color: "var(--text-dim)" }}>Perfil sem permissão de exportação</span>
          )}
        </div>
      </header>

      {/* Os dois selects soltos viraram barra de verdade. O convenio ativo
          continua sendo CONTEXTO GLOBAL do topo e nao reaparece aqui: foi
          decisao do cliente em 30/07/2026, e repetir a dimensao como filtro
          faria a tela parecer ter dois escopos. */}
      <FilterBar
        layout="linha"
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por ADF, nome, CPF ou matrícula"
        {...filtrosUi.propsFilterBar}
        onExport={perfil.perms.exportacao ? () => downloadCsv("carteira-consignataria.csv", exportRows()) : undefined}
        actions={
          <FilterChips
            variant="segmentado"
            label="Situação do contrato"
            value={status}
            // O `FilterChips` fala `string`; o estado e' a uniao estreita. O
            // cast e' seguro porque os `value` das opcoes saem da mesma uniao.
            onChange={(v) => filtrar(setStatus)(v as "" | ContratoStatus)}
            options={[
              { value: "", label: "Todos", count: semStatus.length },
              { value: "em_dia", label: CONTRATO_STATUS_LABEL.em_dia, count: contStatus.em_dia ?? 0 },
              { value: "inadimplente", label: CONTRATO_STATUS_LABEL.inadimplente, count: contStatus.inadimplente ?? 0 },
              { value: "quitado", label: CONTRATO_STATUS_LABEL.quitado, count: contStatus.quitado ?? 0 },
            ]}
          />
        }
      >
        {/* RESULTADO NA FOLHA como dimensao propria, ao lado da situacao do
            contrato. Sao as duas perguntas que a tela confundia: uma e' saude
            financeira, a outra e' se a prefeitura executou o desconto. */}
        <FilterChips
          label="Folha"
          value={folha}
          onChange={(v) => filtrar(setFolha)(v as "" | FolhaBalde)}
          options={[
            { value: "", label: "Todas", count: semFolha.length },
            { value: "aguardando", label: "Aguardando", count: contFolha.aguardando ?? 0 },
            { value: "aplicada", label: "Aplicada", cor: "var(--emerald-500)", count: contFolha.aplicada ?? 0 },
            { value: "falha", label: "Falha", cor: "var(--danger-500)", count: contFolha.falha ?? 0 },
          ]}
        />

        <MaisFiltros ativos={produto ? 1 : 0} resumo={resumoAvancado}>
          <MaisFiltrosGrupo titulo="Produto">
            {/* Desce para o painel porque e' recorte comercial: numa carteira
                de um produto so' ele nao corta nada, e situacao e folha sao o
                que decide a proxima acao. */}
            <SelectField
              label="Produto"
              value={produto}
              onChange={(e) => filtrar(setProduto)(e.target.value as "" | BancoProduto)}
              options={[
                { value: "", label: "Todos" },
                { value: "emprestimo", label: `${PRODUTO_LABEL.emprestimo} (${contProduto.emprestimo ?? 0})` },
                { value: "cartao", label: `${PRODUTO_LABEL.cartao} (${contProduto.cartao ?? 0})` },
                { value: "portabilidade", label: `${PRODUTO_LABEL.portabilidade} (${contProduto.portabilidade ?? 0})` },
              ]}
            />
          </MaisFiltrosGrupo>
        </MaisFiltros>
      </FilterBar>

      {avisoRefin ? <div role="status" style={{ color: "var(--emerald-500)", fontWeight: 600 }}>{avisoRefin}</div> : null}
      {avisoDesaverbar ? <div role="status" style={{ color: "var(--emerald-500)", fontWeight: 600 }}>{avisoDesaverbar}</div> : null}

      {temFiltro ? (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          Mostrando <b style={{ color: "var(--text)" }}>{contratos.length}</b> de {base.length} contratos da carteira.
        </span>
      ) : null}

      {q.isError ? (
        // Erro precede vazio: sem esse aviso, uma falha de fetch mostrava a
        // tabela vazia ("Nenhum contrato") como se a carteira estivesse zerada.
        // Polling de 5s se recupera. Convencao do projeto (30/07/2026).
        <div style={{ padding: "12px 16px", borderRadius: 10, border: "1px solid var(--danger-500)", color: "var(--danger-500)", fontSize: 13, marginBottom: 12 }}>
          Não foi possível carregar os contratos. Tentando novamente…
        </div>
      ) : null}
      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(r) => r.idUnico}
        // Antes dizia SEMPRE "Nenhum contrato com esses filtros", inclusive com
        // os dois selects em "Todos": um banco de carteira vazia era mandado
        // ajustar filtros que nao existiam. Recorte no cliente, entao a tela tem
        // os dois denominadores e a decisao sai do dado (FR-013).
        emptyState={
          <EmptyState
            motivo={motivoVazio(base.length, contratos.length)}
            filtroVazio="Nenhum contrato bate com esses filtros. Limpe os filtros para ver a carteira inteira."
            semDados="Nenhum contrato nesta carteira ainda. Eles aparecem aqui quando você aprova uma proposta."
            onLimpar={filtrosUi.limparFiltros}
          />
        }
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="contratos"
      />

      {cronograma ? <CronogramaLegadoModal adf={cronograma.idUnico} nome={cronograma.nome} totalParcelas={cronograma.parcelas} parcelasPagas={cronograma.parcelasPagas ?? 0} papel="banco" onClose={() => setCronograma(null)} onSalvo={() => { qc.invalidateQueries({ queryKey: ["banco"] }); }} /> : null}
      {informar ? (
        <InformarDadosLegadoModal
          adf={informar.idUnico}
          nome={informar.nome}
          valorParcela={informar.valorParcela}
          totalParcelas={informar.parcelas}
          atual={informar.dadosInformados ?? null}
          papel="banco"
          onClose={() => setInformar(null)}
          onSalvo={() => {
            qc.invalidateQueries({ queryKey: ["banco", "contratos-api"] });
            qc.invalidateQueries({ queryKey: ["banco", "propostas-api"] });
          }}
        />
      ) : null}
      {ofertarRefin ? (
        <OfertarRefinanciamentoModal
          adf={ofertarRefin}
          onClose={() => setOfertarRefin(null)}
          onFeito={(r) => {
            setOfertarRefin(null);
            setAvisoRefin(`Oferta ${r.id} enviada a ${r.servidorNome}. Acompanhe em Propostas › Refinanciamento.`);
            qc.invalidateQueries({ queryKey: ["banco", "refinanciamentos"] });
          }}
        />
      ) : null}
      {desaverbar ? (
        <DesaverbarContratoModal
          adf={desaverbar.idUnico}
          nome={desaverbar.nome}
          onClose={() => setDesaverbar(null)}
          onFeito={() => {
            setAvisoDesaverbar(`Contrato ${desaverbar.idUnico} desaverbado. Folha deste mês permanece; some na próxima competência.`);
            setDesaverbar(null);
            qc.invalidateQueries({ queryKey: ["banco", "contratos-api"] });
          }}
        />
      ) : null}
    </div>
  );
}

/** Celula da coluna CCB — se o contrato tem arquivo anexado (ccbKey), oferece
 *  botao "Abrir" que baixa o blob autenticado do R2 e abre em nova aba.
 *  Sem ccbKey, mostra "—" (contrato averbado antes do upload virar obrigatorio,
 *  ou contrato de reserva que ainda nao teve anexo). */
function CcbCell({ ccbKey }: { ccbKey: string | null }) {
  const [abrindo, setAbrindo] = useState(false);
  if (!ccbKey) return <span style={{ color: "var(--text-dim)", fontSize: 13 }}>—</span>;
  const abrir = async () => {
    setAbrindo(true);
    try {
      const blob = await atlas.banco.fetchCcbBlob(ccbKey);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoga em 30s pra nao vazar memoria em sessoes longas.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      // Silencioso — o operador ve que nada abriu; pode reabrir na pagina de detalhe
      // da proposta, que tem tratamento de erro visivel.
    } finally {
      setAbrindo(false);
    }
  };
  return (
    <Button size="sm" variant="ghost" onClick={abrir} disabled={abrindo}>
      {abrindo ? "..." : "Abrir"}
    </Button>
  );
}
